import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator,
  ChevronRight,
  Clock,
  Eye,
  FileCheck,
  FolderOpen,
  HardHat,
  MapPin,
  User,
  X,
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import ProjectStandupCell from '../components/pm/ProjectStandupCell';
import { useArchEngineerWorkQueues } from '../hooks/useArchEngineerWorkQueues';
import { useArchitectStandupLatestForProjects } from '../hooks/useArchitectStandupLatestByProject';
import { canSubmitArchitectStandup } from '../utils/architectStandupAccess';
import { isDesignerRole } from '../utils/userRoles';
import { buildArchitectActiveProjectGroups } from '../utils/architectStandupProjectGroups';
import { parseLegacyTitle } from '../utils/projectName';
import type { ProjectPhase } from '../types';
import '../components/pm/pm-shared.css';
import '../pages/ProjectHub.css';
import './ArchitectPortal.css';
import './ArchitectStandupPage.css';

type StandupGroup = ReturnType<typeof buildArchitectActiveProjectGroups>[number];

function partitionByPhase(groups: StandupGroup[]) {
  const estimating: StandupGroup[] = [];
  const precon: StandupGroup[] = [];
  const contracted: StandupGroup[] = [];

  for (const group of groups) {
    const phase = group.project.phase || 'contracted';
    if (phase === 'estimating') estimating.push(group);
    else if (phase === 'preconstruction') precon.push(group);
    else contracted.push(group);
  }

  return { estimating, precon, contracted };
}

const ArchitectStandupPage: React.FC = () => {
  const { user } = useAuth();
  const { timberProjects, enhancedDeals, projectTasks, taskGroups, projectOnboardings, crmUsers } =
    useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'projectOnboardings']);
  const navigate = useNavigate();

  const [viewAsExternalId, setViewAsExternalId] = useState<string | null>(null);

  const realUserId = user?.id || '';
  const realIsArchitect = isDesignerRole(user?.role);
  const viewingAsExternal = !realIsArchitect && !!viewAsExternalId;
  const userId = viewingAsExternal ? viewAsExternalId! : realUserId;
  const canWrite = canSubmitArchitectStandup(user?.role) && !viewingAsExternal;

  const architects = useMemo(
    () => crmUsers.filter(u => isDesignerRole(u.role) && u.isActive),
    [crmUsers],
  );

  const viewAsUser = viewAsExternalId ? crmUsers.find(u => u.uid === viewAsExternalId) : null;
  const authorName =
    viewAsUser?.displayName
    || crmUsers.find(u => u.uid === realUserId)?.displayName
    || user?.name
    || user?.email
    || 'Unknown';

  const queuesByUserId = useArchEngineerWorkQueues();
  const dealsById = useMemo(
    () => new Map(enhancedDeals.filter(d => d.id).map(d => [d.id!, d] as const)),
    [enhancedDeals],
  );

  const groups = useMemo(
    () =>
      buildArchitectActiveProjectGroups({
        timberProjects,
        enhancedDeals,
        projectTasks,
        taskGroups,
        projectOnboardings,
        userId,
        queuesByUserId: queuesByUserId[userId] ?? [],
        dealsById,
      }),
    [
      timberProjects,
      enhancedDeals,
      projectTasks,
      taskGroups,
      projectOnboardings,
      userId,
      queuesByUserId,
      dealsById,
    ],
  );

  const { estimating, precon, contracted } = useMemo(() => partitionByPhase(groups), [groups]);

  const projectIds = useMemo(
    () => groups.map(g => g.project.id).filter((id): id is string => !!id),
    [groups],
  );

  const latestByProject = useArchitectStandupLatestForProjects(projectIds);

  const renderRow = (group: StandupGroup) => {
    const { project, deal, openTaskCount } = group;
    if (!project.id) return null;

    const num = (deal?.projectNumber || '').trim();
    const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
    const descriptor = (
      (deal?.descriptor || '').trim()
      || parseLegacyTitle(deal?.title || '').descriptor
      || project.name
      || ''
    ).trim();
    const clientLabel = (deal?.clientLabel || project.clientName || deal?.contactName || '').trim();
    const rowLabel = [numberDisplay, descriptor].filter(Boolean).join(' ').trim();
    const latest = latestByProject.get(project.id) ?? {};

    return (
      <div
        key={project.id}
        className="pm-all-row"
        onClick={() => navigate(`/project-hub/${project.id}`)}
      >
        <div className="pm-all-row-main">
          <div className="pm-all-row-name">
            {numberDisplay && <span className="pm-all-row-number">{numberDisplay}</span>}
            <span className="pm-all-row-descriptor">{descriptor || project.name}</span>
          </div>
          {(clientLabel || project.location) && (
            <div className="pm-all-row-meta">
              {clientLabel && (
                <span className="pm-all-row-client">
                  <User size={10} aria-hidden />
                  <span className="pm-all-row-client-label">client</span>
                  <span className="pm-all-row-client-name">{clientLabel}</span>
                </span>
              )}
              {project.location && (
                <span className="pm-all-row-loc">
                  <MapPin size={10} /> {project.location}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="pm-all-row-standup" onClick={e => e.stopPropagation()}>
          <ProjectStandupCell
            projectId={project.id}
            projectLabel={rowLabel}
            latest={latest}
            currentUserId={canWrite ? realUserId : userId}
            currentUserName={authorName}
            variant="architect"
            readOnly={!canWrite}
            placeholder="Status for Project Manager…"
            historyTitle="Designer report history"
          />
        </div>
        <div className="pm-all-row-stats">
          <span className="pm-all-row-badge">
            <Clock size={9} aria-hidden />
            {openTaskCount} open
          </span>
        </div>
        <div className="pm-all-row-progress" aria-hidden="true" />
        <div className="pm-all-row-comments-cell" aria-hidden="true" />
        <ChevronRight size={14} className="pm-all-row-arrow" />
      </div>
    );
  };

  const renderSection = (
    phaseGroups: StandupGroup[],
    phase: ProjectPhase,
    label: string,
    icon: React.ReactNode,
    headerClass: string,
  ) => {
    if (phaseGroups.length === 0) return null;
    return (
      <div className="pm-all-section" key={phase}>
        <div className={`pm-all-section-header ${headerClass}`}>
          {icon}
          <span>{label}</span>
          <span className="pm-all-section-count">{phaseGroups.length}</span>
        </div>
        {phaseGroups.map(renderRow)}
      </div>
    );
  };

  return (
    <div className="pm-hub ap-standup-page">
      <ArchitectHeader />
      <div className="pm-hub-layout ap-standup-layout">
        <main className="pm-hub-main">
          <div className="pm-hub-content pm-scroll">
            <div className="ap-standup-centered">
              <header className="ap-standup-header">
                <h1 className="ap-standup-title">Designer standup</h1>
                <p className="ap-standup-subtitle">
                  Quick update for your project manager — one note per project where you have open tasks.
                </p>
              </header>

              {!realIsArchitect && architects.length > 0 && (
                <div className="ap-view-as ap-standup-view-as">
                {viewingAsExternal ? (
                  <div className="ap-view-as-banner">
                    <Eye size={14} />
                    <span>
                      Previewing as <strong>{viewAsUser?.displayName}</strong>
                    </span>
                    <button
                      type="button"
                      className="ap-view-as-clear"
                      onClick={() => setViewAsExternalId(null)}
                      title="Exit preview"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="ap-view-as-picker">
                    <Eye size={14} />
                    <select
                      value=""
                      onChange={e => {
                        if (e.target.value) setViewAsExternalId(e.target.value);
                      }}
                    >
                      <option value="">Preview as designer…</option>
                      {architects.map(a => (
                        <option key={a.uid} value={a.uid}>
                          {a.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              )}

              {groups.length === 0 ? (
                <div className="ap-standup-empty">
                  <FolderOpen size={32} />
                  <p>No projects with open tasks assigned to you.</p>
                </div>
              ) : (
                <div className="pm-all-list ap-standup-list">
                  {renderSection(estimating, 'estimating', 'Estimating', <Calculator size={14} />, 'pm-all-section-estimating')}
                  {renderSection(precon, 'preconstruction', 'Preconstruction', <HardHat size={14} />, 'pm-all-section-precon')}
                  {renderSection(contracted, 'contracted', 'Under Contract', <FileCheck size={14} />, 'pm-all-section-contract')}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ArchitectStandupPage;
